import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useEffect, useRef, useState } from 'react';
import { FALLBACK_PROJECT_COLOR, secondsToHMS } from 'shared/components/charts';
import { avatarColor, nameInitials } from 'shared/utils/avatarUtils';
import { formatDateRange } from 'shared/utils/dateUtils';

import {
  MiniBar,
  MiniBarStack,
  SortIconSpan,
  TeamActivityCell,
  TeamActivityColorDot,
  TeamActivityDescription,
  TeamActivitySubRow,
  TeamActivitySubText,
  TeamActivityTimeText,
  TeamBarProjectCount,
  TeamCard,
  TeamCardBadge,
  TeamCardHeader,
  TeamCardSubtitle,
  TeamCardTitle,
  TeamCardTitleBox,
  TeamEmptyRow,
  TeamHeaderCell,
  TeamHeaderGroup,
  TeamMemberAvatar,
  TeamMemberCell,
  TeamMemberName,
  TeamMemberNameWrap,
  TeamMemberSub,
  TeamMiniBarTrack,
  TeamRow,
  TeamTableHeader,
  TeamTimeCell,
  TeamTimeCellValue,
  TeamTooltipBox,
  TeamTooltipDot,
  TeamTooltipDuration,
  TeamTooltipName,
  TeamTooltipPct,
  TeamTooltipRow,
  TeamTooltipTitle,
  teamBarTooltipArrowSx,
  teamBarTooltipSx,
} from '../dashboard.styles';
import type { TeamActivityEntry } from '../types/dashboard.types';
import { formatHoursMinutes } from '../utils/dashboardUtils';

dayjs.extend(relativeTime);

type SortKey = 'name' | 'activity' | 'time';
type SortDir = 'asc' | 'desc';

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  return (
    <SortIconSpan active={active}>
      {active && dir === 'asc' ? '▲' : '▼'}
    </SortIconSpan>
  );
}

// Live ticking timer for running entries
const RunningTimer: React.FC<{ startedAt: string }> = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(() =>
    Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000),
  );
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    ref.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
    }, 1000);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [startedAt]);

  return <>{secondsToHMS(elapsed)}</>;
};

interface ProjectBarTooltipProps {
  member: TeamActivityEntry;
  children: React.ReactElement;
}

const ProjectBarTooltip: React.FC<ProjectBarTooltipProps> = ({ member, children }) => {
  const title = (
    <TeamTooltipBox>
      <TeamTooltipTitle>
        {member.name} · {secondsToHMS(member.totalTrackedSecs)}
      </TeamTooltipTitle>
      {member.topProjects.map((p) => {
        const pct = member.totalTrackedSecs > 0
          ? ((p.totalSecs / member.totalTrackedSecs) * 100).toFixed(2)
          : '0.00';
        return (
          <TeamTooltipRow key={p.projectId}>
            <TeamTooltipDot dotcolor={p.color ?? FALLBACK_PROJECT_COLOR} />
            <TeamTooltipName noWrap>{p.name}</TeamTooltipName>
            <TeamTooltipDuration>{secondsToHMS(p.totalSecs)}</TeamTooltipDuration>
            <TeamTooltipPct>{pct}%</TeamTooltipPct>
          </TeamTooltipRow>
        );
      })}
    </TeamTooltipBox>
  );

  return (
    <Tooltip
      title={title}
      placement="top"
      arrow
      slotProps={{
        tooltip: { sx: teamBarTooltipSx },
        arrow: { sx: teamBarTooltipArrowSx },
      }}
    >
      {children}
    </Tooltip>
  );
};

interface Props {
  members: TeamActivityEntry[];
  from: string;
  to: string;
}

const DashboardTeamActivity: React.FC<Props> = ({ members, from, to }) => {
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sorted = [...members].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'name') {
      cmp = a.name.localeCompare(b.name);
    } else if (sortKey === 'activity') {
      const da = a.latestDescription ?? '';
      const db = b.latestDescription ?? '';
      cmp = da.localeCompare(db);
    } else {
      cmp = a.totalTrackedSecs - b.totalTrackedSecs;
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const maxTrackedSecs = members.reduce((m, r) => Math.max(m, r.totalTrackedSecs), 1);

  const headerCell = (label: string, key: SortKey) => (
    <TeamHeaderCell onClick={() => handleSort(key)}>
      {label}
      <SortIcon active={sortKey === key} dir={sortDir} />
    </TeamHeaderCell>
  );

  return (
    <TeamCard>
      <TeamCardHeader>
        <TeamCardTitleBox>
          <TeamCardTitle>Team Activity</TeamCardTitle>
          <TeamCardSubtitle>What each teammate is working on and how their time breaks down.</TeamCardSubtitle>
        </TeamCardTitleBox>
        <TeamCardBadge>{members.length} members</TeamCardBadge>
      </TeamCardHeader>

      <TeamTableHeader>
        {headerCell('Team member', 'name')}
        {headerCell('Latest activity', 'activity')}
        <TeamHeaderGroup>
          <TeamHeaderCell onClick={() => handleSort('time')}>
            Total tracked ({formatDateRange(from, to)})
            <SortIcon active={sortKey === 'time'} dir={sortDir} />
          </TeamHeaderCell>
        </TeamHeaderGroup>
      </TeamTableHeader>

      {sorted.map((member) => {
        const memberScale = member.totalTrackedSecs / maxTrackedSecs;
        const bgColor = avatarColor(member.name);

        return (
          <TeamRow key={member.userId}>
            {/* Member */}
            <TeamMemberCell>
              <TeamMemberAvatar src={member.avatarUrl} bgcolor={bgColor}>
                {nameInitials(member.name)}
              </TeamMemberAvatar>
              <TeamMemberNameWrap>
                <TeamMemberName>{member.name}</TeamMemberName>
              </TeamMemberNameWrap>
            </TeamMemberCell>

            {/* Latest activity */}
            <TeamActivityCell>
              {member.latestDescription ? (
                <>
                  <Tooltip title={member.latestDescription} placement="top">
                    <TeamActivityDescription>
                      {member.latestDescription}
                    </TeamActivityDescription>
                  </Tooltip>
                  <TeamActivitySubRow>
                    {member.latestProjectColor && (
                      <TeamActivityColorDot dotcolor={member.latestProjectColor} />
                    )}
                    <TeamActivitySubText>
                      {member.latestProjectName ?? ''}
                      {member.latestClientName ? ` · ${member.latestClientName}` : ''}
                    </TeamActivitySubText>
                    <TeamActivityTimeText running={member.latestIsRunning}>
                      {member.latestIsRunning && member.latestStartedAt
                        ? <>· <RunningTimer startedAt={member.latestStartedAt} /> · In progress</>
                        : member.latestDurationSecs != null
                          ? <>· {secondsToHMS(member.latestDurationSecs)}{member.latestStartedAt ? ` · ${dayjs(member.latestStartedAt).fromNow()}` : ''}</>
                          : member.latestStartedAt
                            ? `· ${dayjs(member.latestStartedAt).fromNow()}`
                            : ''}
                    </TeamActivityTimeText>
                  </TeamActivitySubRow>
                </>
              ) : (
                <TeamMemberSub>—</TeamMemberSub>
              )}
            </TeamActivityCell>

            {/* Total tracked + project bar merged */}
            <TeamTimeCell>
              <TeamTimeCellValue>
                {formatHoursMinutes(member.totalTrackedSecs)}
              </TeamTimeCellValue>
              <ProjectBarTooltip member={member}>
                <TeamMiniBarTrack>
                  {member.topProjects.length > 0 && (
                    <MiniBarStack>
                      {member.topProjects.map((p) => (
                        <MiniBar
                          key={p.projectId}
                          barcolor={p.color ?? FALLBACK_PROJECT_COLOR}
                          widthpct={Math.round((p.totalSecs / member.totalTrackedSecs) * memberScale * 100)}
                        />
                      ))}
                    </MiniBarStack>
                  )}
                </TeamMiniBarTrack>
              </ProjectBarTooltip>
              {member.topProjects.length > 0 && (
                <TeamBarProjectCount>
                  {member.topProjects.length} project{member.topProjects.length !== 1 ? 's' : ''}
                </TeamBarProjectCount>
              )}
            </TeamTimeCell>
          </TeamRow>
        );
      })}

      {members.length === 0 && (
        <TeamEmptyRow>No activity in selected period</TeamEmptyRow>
      )}
    </TeamCard>
  );
};

export default DashboardTeamActivity;
